import { zodResolver } from '@hookform/resolvers/zod';
import { fromZonedTime } from 'date-fns-tz';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '@apps/web/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@apps/web/components/ui/dialog';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@apps/web/components/ui/form';
import { Input } from '@apps/web/components/ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@apps/web/components/ui/select';
import { getApiClient } from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';

const DAY_OPTIONS = [
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
    'sunday',
] as const;

const DAY_LABELS: Record<string, string> = {
    monday: 'Monday',
    tuesday: 'Tuesday',
    wednesday: 'Wednesday',
    thursday: 'Thursday',
    friday: 'Friday',
    saturday: 'Saturday',
    sunday: 'Sunday',
};

const TIME_PATTERN = /^\d{2}:\d{2}$/;

function parseTimeToMinutes(
    value: string,
    options?: { readonly allow24Hour?: boolean },
): number | null {
    if (!TIME_PATTERN.test(value)) {
        return null;
    }
    const [hoursPart, minutesPart] = value.split(':');
    const hours = Number(hoursPart);
    const minutes = Number(minutesPart);
    if (!Number.isInteger(hours) || !Number.isInteger(minutes)) {
        return null;
    }
    if (minutes < 0 || minutes > 59) {
        return null;
    }
    if (hours === 24) {
        if (options?.allow24Hour === true && minutes === 0) {
            return 24 * 60;
        }
        return null;
    }
    if (hours < 0 || hours > 23) {
        return null;
    }
    return hours * 60 + minutes;
}

const standardTimeSchema = z
    .string()
    .regex(TIME_PATTERN, 'Use HH:MM format')
    .refine((value) => parseTimeToMinutes(value) !== null, {
        message: 'Use HH:MM format',
    });

const weeklyEndTimeSchema = z
    .string()
    .regex(TIME_PATTERN, 'Use HH:MM format')
    .refine(
        (value) => parseTimeToMinutes(value, { allow24Hour: true }) !== null,
        {
            message: 'Use HH:MM format',
        },
    );

const oneTimeSchema = z
    .object({
        mode: z.literal('one-time'),
        status: z.enum(['available', 'unavailable']),
        start_date: z.string().min(1, 'Start date is required'),
        start_time: standardTimeSchema,
        end_date: z.string().min(1, 'End date is required'),
        end_time: standardTimeSchema,
    })
    .refine(
        (v) =>
            `${v.end_date}T${v.end_time}` > `${v.start_date}T${v.start_time}`,
        {
            message: 'End must be after start',
            path: ['end_time'],
        },
    );

const weeklySchema = z
    .object({
        mode: z.literal('weekly'),
        status: z.enum(['available', 'unavailable']),
        day_of_week: z.enum(DAY_OPTIONS),
        start_time: standardTimeSchema,
        end_time: weeklyEndTimeSchema,
    })
    .refine(
        (v) => {
            const startMinutes = parseTimeToMinutes(v.start_time);
            const endMinutes = parseTimeToMinutes(v.end_time, {
                allow24Hour: true,
            });
            if (startMinutes === null || endMinutes === null) {
                return false;
            }
            return endMinutes > startMinutes;
        },
        {
            message: 'End must be after start',
            path: ['end_time'],
        },
    );

const formSchema = z.discriminatedUnion('mode', [oneTimeSchema, weeklySchema]);

type FormValues = z.infer<typeof formSchema>;

export type EditTarget =
    | {
          readonly type: 'one-time';
          readonly id: string;
          readonly status: 'available' | 'unavailable';
          readonly startDate: string;
          readonly startTime: string;
          readonly endDate: string;
          readonly endTime: string;
      }
    | {
          readonly type: 'weekly';
          readonly id: string;
          readonly status: 'available' | 'unavailable';
          readonly dayOfWeek: string;
          readonly startTime: string;
          readonly endTime: string;
      };

type AvailabilityFormDialogProps = {
    readonly open: boolean;
    readonly onOpenChange: (open: boolean) => void;
    readonly organizationId: string;
    readonly caregiverId: string;
    readonly timezone: string;
    readonly editTarget?: EditTarget;
    readonly onSuccess: () => void;
};

const ONE_TIME_DEFAULTS: FormValues = {
    mode: 'one-time',
    status: 'available',
    start_date: '',
    start_time: '09:00',
    end_date: '',
    end_time: '17:00',
};

const WEEKLY_DEFAULTS: FormValues = {
    mode: 'weekly',
    status: 'available',
    day_of_week: 'monday',
    start_time: '09:00',
    end_time: '17:00',
};

function getDefaultValues(editTarget?: EditTarget): FormValues {
    if (!editTarget) {
        return ONE_TIME_DEFAULTS;
    }
    if (editTarget.type === 'one-time') {
        return {
            mode: 'one-time',
            status: editTarget.status,
            start_date: editTarget.startDate,
            start_time: editTarget.startTime,
            end_date: editTarget.endDate,
            end_time: editTarget.endTime,
        };
    }
    return {
        mode: 'weekly',
        status: editTarget.status,
        day_of_week: editTarget.dayOfWeek as (typeof DAY_OPTIONS)[number],
        start_time: editTarget.startTime,
        end_time: editTarget.endTime,
    };
}

export function AvailabilityFormDialog({
    open,
    onOpenChange,
    organizationId,
    caregiverId,
    timezone,
    editTarget,
    onSuccess,
}: AvailabilityFormDialogProps) {
    const defaults = getDefaultValues(editTarget);
    const form = useForm<FormValues>({
        resolver: zodResolver(formSchema),
        defaultValues: defaults,
    });

    useEffect(() => {
        if (open) {
            form.reset(getDefaultValues(editTarget));
        }
    }, [open, form, editTarget]);

    const mode = form.watch('mode');
    const isEdit = !!editTarget;

    const onSubmit = async (values: FormValues) => {
        try {
            if (values.mode === 'one-time') {
                const startIso = fromZonedTime(
                    `${values.start_date}T${values.start_time}:00`,
                    timezone,
                ).toISOString();
                const endIso = fromZonedTime(
                    `${values.end_date}T${values.end_time}:00`,
                    timezone,
                ).toISOString();
                const body = {
                    status: values.status,
                    start_time: startIso,
                    end_time: endIso,
                };

                if (isEdit && editTarget.type === 'one-time') {
                    await getApiClient().put({
                        url: '/organizations/{organization_id}/caregivers/{caregiver_id}/availability/{availability_id}',
                        pathParams: {
                            organization_id: organizationId,
                            caregiver_id: caregiverId,
                            availability_id: editTarget.id,
                        },
                        body,
                    });
                } else {
                    await getApiClient().post({
                        url: '/organizations/{organization_id}/caregivers/{caregiver_id}/availability',
                        pathParams: {
                            organization_id: organizationId,
                            caregiver_id: caregiverId,
                        },
                        body,
                    });
                }
            } else {
                const body = {
                    status: values.status,
                    day_of_week: values.day_of_week,
                    start_time: values.start_time,
                    end_time: values.end_time,
                };

                if (isEdit && editTarget.type === 'weekly') {
                    await getApiClient().put({
                        url: '/organizations/{organization_id}/caregivers/{caregiver_id}/weekly-availability/{weekly_availability_id}',
                        pathParams: {
                            organization_id: organizationId,
                            caregiver_id: caregiverId,
                            weekly_availability_id: editTarget.id,
                        },
                        body,
                    });
                } else {
                    await getApiClient().post({
                        url: '/organizations/{organization_id}/caregivers/{caregiver_id}/weekly-availability',
                        pathParams: {
                            organization_id: organizationId,
                            caregiver_id: caregiverId,
                        },
                        body,
                    });
                }
            }

            toast.success(
                isEdit ? 'Availability updated' : 'Availability added',
                { persistNotification: false },
            );
            onOpenChange(false);
            onSuccess();
        } catch (error) {
            const fallback = isEdit
                ? 'Failed to update availability'
                : 'Failed to add availability';
            toast.error(
                error instanceof Error && error.message
                    ? error.message
                    : fallback,
                { persistNotification: false },
            );
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[480px]">
                <DialogHeader>
                    <DialogTitle>
                        {isEdit ? 'Edit Availability' : 'Add Availability'}
                    </DialogTitle>
                </DialogHeader>
                <Form {...form}>
                    <form
                        onSubmit={form.handleSubmit(onSubmit)}
                        className="space-y-4"
                    >
                        {!isEdit && (
                            <FormField
                                control={form.control}
                                name="mode"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Type</FormLabel>
                                        <Select
                                            value={field.value}
                                            onValueChange={(v) => {
                                                if (v === 'one-time') {
                                                    form.reset(
                                                        ONE_TIME_DEFAULTS,
                                                    );
                                                } else {
                                                    form.reset(WEEKLY_DEFAULTS);
                                                }
                                            }}
                                            disabled={
                                                form.formState.isSubmitting
                                            }
                                        >
                                            <FormControl>
                                                <SelectTrigger>
                                                    <SelectValue />
                                                </SelectTrigger>
                                            </FormControl>
                                            <SelectContent>
                                                <SelectItem value="one-time">
                                                    One-time
                                                </SelectItem>
                                                <SelectItem value="weekly">
                                                    Weekly
                                                </SelectItem>
                                            </SelectContent>
                                        </Select>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        )}
                        <FormField
                            control={form.control}
                            name="status"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Status</FormLabel>
                                    <Select
                                        value={field.value}
                                        onValueChange={field.onChange}
                                        disabled={form.formState.isSubmitting}
                                    >
                                        <FormControl>
                                            <SelectTrigger>
                                                <SelectValue />
                                            </SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                            <SelectItem value="available">
                                                Available
                                            </SelectItem>
                                            <SelectItem value="unavailable">
                                                Unavailable
                                            </SelectItem>
                                        </SelectContent>
                                    </Select>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        {mode === 'one-time' ? (
                            <div className="grid grid-cols-2 items-start gap-4">
                                <FormField
                                    control={form.control}
                                    name="start_date"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Start date</FormLabel>
                                            <FormControl>
                                                <Input
                                                    type="date"
                                                    {...field}
                                                    disabled={
                                                        form.formState
                                                            .isSubmitting
                                                    }
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name="start_time"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Start time</FormLabel>
                                            <FormControl>
                                                <Input
                                                    type="time"
                                                    {...field}
                                                    disabled={
                                                        form.formState
                                                            .isSubmitting
                                                    }
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name="end_date"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>End date</FormLabel>
                                            <FormControl>
                                                <Input
                                                    type="date"
                                                    {...field}
                                                    disabled={
                                                        form.formState
                                                            .isSubmitting
                                                    }
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name="end_time"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>End time</FormLabel>
                                            <FormControl>
                                                <Input
                                                    type="time"
                                                    {...field}
                                                    disabled={
                                                        form.formState
                                                            .isSubmitting
                                                    }
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>
                        ) : (
                            <div className="space-y-4">
                                <FormField
                                    control={form.control}
                                    name="day_of_week"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Day of week</FormLabel>
                                            <Select
                                                value={field.value}
                                                onValueChange={field.onChange}
                                                disabled={
                                                    form.formState.isSubmitting
                                                }
                                            >
                                                <FormControl>
                                                    <SelectTrigger>
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                </FormControl>
                                                <SelectContent>
                                                    {DAY_OPTIONS.map((day) => (
                                                        <SelectItem
                                                            key={day}
                                                            value={day}
                                                        >
                                                            {DAY_LABELS[day]}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <div className="grid grid-cols-2 items-start gap-4">
                                    <FormField
                                        control={form.control}
                                        name="start_time"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>
                                                    Start time
                                                </FormLabel>
                                                <FormControl>
                                                    <Input
                                                        type="time"
                                                        {...field}
                                                        disabled={
                                                            form.formState
                                                                .isSubmitting
                                                        }
                                                    />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                    <FormField
                                        control={form.control}
                                        name="end_time"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>End time</FormLabel>
                                                <FormControl>
                                                    <Input
                                                        type="time"
                                                        {...field}
                                                        value={
                                                            field.value ===
                                                            '24:00'
                                                                ? '23:59'
                                                                : field.value
                                                        }
                                                        onChange={(e) => {
                                                            field.onChange(
                                                                e.target.value,
                                                            );
                                                        }}
                                                        disabled={
                                                            form.formState
                                                                .isSubmitting
                                                        }
                                                    />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                </div>
                            </div>
                        )}
                        <DialogFooter>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => onOpenChange(false)}
                                disabled={form.formState.isSubmitting}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                disabled={form.formState.isSubmitting}
                            >
                                {isEdit ? 'Save' : 'Add'}
                            </Button>
                        </DialogFooter>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    );
}
